import { Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import { Link } from 'wouter';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { subscribeNewsletter } from '@/lib/api/newsletter';

export function SiteFooter() {
  const [email, setEmail] = useState('');
  const { toast } = useToast();

  // Data for social icons
  const socialIcons = [
    // { href: '#', src: MessageCircle, alt: 'Direct Message' },
    {
      href: 'https://www.instagram.com/legisiq/',
      src: Instagram,
      alt: 'Instagram',
    },
    {
      href: 'https://www.facebook.com/legisiq',
      src: Facebook,
      alt: 'Facebook',
    },
    { href: 'https://x.com/legisiq', src: Twitter, alt: 'Twitter' },
    {
      href: 'https://www.linkedin.com/company/legisiq',
      src: Linkedin,
      alt: 'LinkedIn',
    },
  ];

  // Footer navigation links
  const footerLinks = [
    {
      title: 'Product',
      links: [{ label: 'Features', href: '/features' }],
    },
    {
      title: 'Agents',
      links: [
        { label: 'Bill Agent', href: '/products/bill-agent' },
        { label: 'Committee Agent', href: '/products/committee-agent' },
      ],
    },
    {
      title: 'Portals',
      links: [
        { label: 'Bills', href: '/portals/bills' },
        { label: 'Members', href: '/portals/members' },
        { label: 'Committees', href: '/portals/committees' },
        { label: 'Transcript Hub', href: '/portals/transcript-hub' },
        { label: 'Lobby', href: '/portals/lobby' },
        { label: 'Statutes', href: '/portals/statutes' },
        { label: 'Campaign Finance', href: '/portals/campaign-finance' },
      ],
    },
    {
      title: 'Tools',
      links: [
        { label: 'Agentic Track', href: '/products/auto-track' },
        { label: 'Advocate Management', href: '/advocate-management' },
        { label: 'AI Bill Brief', href: '/ai-reports' },
        { label: 'Bill Comparison', href: '/products/bill-comparison' },
        { label: 'Live Alerts', href: '/live-actions' },
      ],
    },
    {
      title: 'Solutions',
      links: [
        { label: 'For Lobbyists', href: '/solutions/lobbyists' },
        { label: 'For Associations', href: '/solutions/associations' },
        { label: 'For Corporations', href: '/solutions/corporations' },
      ],
    },
    {
      title: 'Company',
      links: [
        { label: 'About', href: '/about' },
        { label: 'Why OneGov', href: '/why-onegov' },
        { label: 'Blog', href: '/blog' },
        { label: 'Help', href: '/help' },
      ],
    },
  ];

  return (
    <footer className="bg-gradient-to-b from-white to-slate-50 py-8 md:py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="mx-auto w-full max-w-7xl">
        {/* Main Content - Flex layout that stays in one row until wrap is needed */}
        <div className="mb-12 flex flex-col gap-8 md:flex-row md:gap-12">
          {/* Logo and Description Section */}
          <div className="flex flex-shrink-0 flex-col items-center text-center md:items-start md:text-left md:w-80">
            <div className="mb-4 w-full">
              <Link href="/">
                <img
                  src="/onegov-logo.png"
                  alt="OneGov Logo"
                  className="mx-auto h-auto w-40 sm:w-48 md:mx-0 md:w-56"
                />
              </Link>
            </div>
            <p className="text-base text-gray-700">
              Empowering government affairs teams with intelligent legislative tracking and
              analysis.
            </p>
          </div>

          {/* Footer Links - Flex grid that wraps naturally */}
          <div className="flex flex-1 flex-wrap gap-6 sm:gap-8">
            {footerLinks.map((group, index) => (
              <div key={index} className="min-w-[140px] flex-shrink-0">
                <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-gray-900">
                  {group.title}
                </h3>
                <ul className="space-y-2">
                  {group.links.map((link, i) => (
                    <li key={i}>
                      <Link
                        href={link.href}
                        className="text-base text-gray-600 hover:text-[#102A43]"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Newsletter Section */}
          <div className="flex flex-shrink-0 flex-col md:w-80">
            <h3 className="mb-4 text-sm font-semibold text-gray-600">Stay up to date</h3>
            <div className="flex flex-col gap-3">
              <Input
                placeholder="Enter your email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="min-w-0 border border-gray-300 bg-transparent placeholder:font-medium placeholder:text-gray-400"
              />
              <Button
                size="lg"
                className="whitespace-nowrap bg-[#102A43] font-semibold text-white hover:bg-[#0d1f32]"
                onClick={async () => {
                  try {
                    await subscribeNewsletter({ subscribeNewsletterRequest: { email } });
                    toast({ description: 'Subscribed!' });
                    setEmail('');
                  } catch {
                    toast({ description: 'Subscription failed' });
                  }
                }}
              >
                Subscribe
              </Button>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-gray-200 pt-8 md:flex-row md:gap-6">
          <p className="text-center text-base text-[#717680] md:text-left">
            © 2026 OneGov. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
            <Link
              href="/termsandprivacy"
              className="text-base font-medium text-gray-600 hover:text-[#102A43]"
            >
              Terms & Privacy
            </Link>
            {socialIcons.map((icon, index) => (
              <a
                href={icon.href}
                key={index}
                target={icon.href.startsWith('http') ? '_blank' : undefined}
                rel={icon.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                className="text-gray-600 hover:text-[#102A43]"
              >
                <icon.src />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

export default SiteFooter;
